import { CareerFocus, CareerFocusWithMetrics } from '../hooks/useCareerFocus';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'internshipDuration' });

/**
 * Get the total duration of an internship in weeks
 * Calculates based on start_date and end_date
 * @param focus - The career focus object
 * @returns Total weeks duration, defaults to 12 if dates are invalid
 */
export const getInternshipDuration = (focus: CareerFocus | CareerFocusWithMetrics | null | undefined): number => {
  if (!focus) return 12; // Default fallback
  
  // If it already has total_weeks calculated (CareerFocusWithMetrics), use it
  if ('total_weeks' in focus && focus.total_weeks) {
    return focus.total_weeks;
  }
  
  // Otherwise calculate from dates
  if (!focus.start_date || !focus.end_date) {
    return 12; // Default fallback if dates missing
  }
  
  try {
    const startDate = new Date(focus.start_date);
    const endDate = new Date(focus.end_date);
    
    // Validate dates
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return 12;
    }
    
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
    const totalWeeks = Math.ceil(totalDays / 7);
    
    // Reasonable bounds check (2-52 weeks)
    if (totalWeeks < 2 || totalWeeks > 52) {
      return 12;
    }
    
    return totalWeeks;
  } catch (error) {
    logger.error('Error calculating internship duration:', error);
    return 12;
  }
};

/**
 * Get the current week number of an internship
 * @param focus - The career focus object
 * @returns Current week number, minimum 1
 */
export const getCurrentWeek = (focus: CareerFocus | CareerFocusWithMetrics | null | undefined): number => {
  if (!focus) return 1;
  
  // If it already has current_week calculated (CareerFocusWithMetrics), use it
  if ('current_week' in focus && focus.current_week) {
    return focus.current_week;
  }
  
  // Otherwise calculate from start date
  if (!focus.start_date) return 1;
  
  try {
    const startDate = new Date(focus.start_date);
    const now = new Date();
    
    if (isNaN(startDate.getTime())) return 1;
    
    const daysElapsed = Math.ceil((now.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
    const currentWeek = Math.max(1, Math.ceil(daysElapsed / 7));
    const totalWeeks = getInternshipDuration(focus);
    
    return Math.min(totalWeeks, currentWeek);
  } catch (error) {
    logger.error('Error calculating current week:', error);
    return 1;
  }
};

/**
 * Calculate dynamic phase boundaries based on internship duration
 * @param totalWeeks - Total duration in weeks
 * @returns Object with week ranges for each phase
 */
export const calculatePhaseBoundaries = (totalWeeks: number) => {
  // For shorter internships (8 weeks or less)
  if (totalWeeks <= 8) {
    return {
      onboarding: { start: 1, end: 1, label: 'Onboarding' },
      foundation: { start: 2, end: 2, label: 'Foundation' },
      growth: { start: 3, end: 5, label: 'Growth' },
      impact: { start: 6, end: 7, label: 'Impact' },
      transition: { start: 8, end: totalWeeks, label: 'Transition' }
    };
  }
  
  // For standard internships (9-12 weeks)
  if (totalWeeks <= 12) {
    const foundationEnd = Math.floor(totalWeeks * 0.25); // ~25%
    const growthEnd = Math.floor(totalWeeks * 0.58); // ~58%
    const impactEnd = Math.floor(totalWeeks * 0.83); // ~83%
    
    return {
      onboarding: { start: 1, end: 1, label: 'Onboarding' },
      foundation: { start: 2, end: foundationEnd, label: 'Foundation' },
      growth: { start: foundationEnd + 1, end: growthEnd, label: 'Growth' },
      impact: { start: growthEnd + 1, end: impactEnd, label: 'Impact' },
      transition: { start: impactEnd + 1, end: totalWeeks, label: 'Transition' }
    };
  }
  
  // For longer internships (13+ weeks)
  const foundationEnd = 3;
  const growthEnd = Math.floor(totalWeeks * 0.5); // 50%
  const impactEnd = totalWeeks - 2; // Leave 2 weeks for transition
  
  return {
    onboarding: { start: 1, end: 1, label: 'Onboarding' },
    foundation: { start: 2, end: foundationEnd, label: 'Foundation' },
    growth: { start: foundationEnd + 1, end: growthEnd, label: 'Growth' },
    impact: { start: growthEnd + 1, end: impactEnd, label: 'Impact' },
    transition: { start: impactEnd + 1, end: totalWeeks, label: 'Transition' }
  };
};

